import React from 'react'
import {useTranslation} from 'react-i18next'
import {CrmTask, taskPipeline, TasksQuery, TeamMember} from '../../domain/entities/Pipeline.ts'
import {useB2BAgencies} from '../../hooks/useB2BAgencies.ts'
import {useCrmTasks} from '../../hooks/usePipelines.ts'
import {cardTravelerName, dateInputToUtcMidnight, daysFromToday, DEFAULT_MARKETS, millisToDateInput, relativeDue, touchLabel, useDateFormatters} from './pipelineView.ts'
import {MultiSelectFilter} from './MultiSelectFilter.tsx'
import {Avatar, ChannelIcon, Countries, MarketChip, Spinner, Tag} from './PipelineParts.tsx'

type Section = 'overdue' | 'today' | 'upcoming'

const taskDue = (task: CrmTask) => task.touchpoint.dueAt ?? task.touchpoint.scheduledAt ?? null

function sectionOf(task: CrmTask, now: number): Section {
    const due = taskDue(task)
    if (task.overdue || (due != null && due < now)) return 'overdue'
    if (due != null && daysFromToday(due, now) <= 0) return 'today'
    return 'upcoming'
}

// My tasks: scheduled human touches and Fix failed contact tasks of both
// pipelines (GET /crm/tasks), already sorted by date by the backend; grouped
// into Overdue, Today, Upcoming. Opening one opens its pipeline's panel.
export const MyTasks: React.FC<{
    query: TasksQuery
    onQueryChange: (query: TasksQuery) => void
    team: TeamMember[]
    selectedId: string | null
    onOpenTask: (task: CrmTask) => void
}> = ({query, onQueryChange, team, selectedId, onOpenTask}) => {
    const {t} = useTranslation('dashboard')
    const {formatDateTime} = useDateFormatters()
    const {facets} = useB2BAgencies()
    const {tasks, isLoadingTasks, isFetchingTasks, tasksError, hasMoreTasks, isFetchingMoreTasks, loadMoreTasks, refetchTasks} = useCrmTasks(query)
    const set = (patch: Partial<TasksQuery>) => onQueryChange({...query, ...patch})
    const markets = facets?.markets?.length ? facets.markets : DEFAULT_MARKETS
    const hasFilters = !!query.market?.length || !!query.kind || (!!query.pipeline && query.pipeline !== 'all') || query.from != null || query.to != null
    const dateClass = (active: boolean) => `h-9 px-2.5 rounded-full border bg-white text-[12.5px] ${active ? 'border-neutralBlack-900' : 'border-neutralBlack-200'}`
    const now = Date.now()
    const sections: Record<Section, CrmTask[]> = {overdue: [], today: [], upcoming: []}
    tasks.forEach((task) => sections[sectionOf(task, now)].push(task))

    return (
        <div className="flex-1 min-h-0 flex flex-col gap-3.5">
            <div className="flex items-center gap-3 flex-wrap flex-none">
                <label className="inline-flex items-center gap-2 text-[13px] text-neutralBlack-700">
                    {t('pipelines.tasks.person')}
                    <select value={query.assignee} onChange={(e) => set({assignee: e.target.value})}
                            className="h-9 pl-3 pr-8 rounded-full border border-neutralBlack-200 bg-white text-[13px] font-medium text-neutralBlack-900">
                        <option value="me">{t('pipelines.tasks.me')}</option>
                        <option value="all">{t('pipelines.tasks.all')}</option>
                        <option value="unassigned">{t('pipelines.unassigned')}</option>
                        {team.map((m) => <option key={m.id} value={m.id}>{m.name}{m.role ? ` · ${m.role}` : ''}</option>)}
                    </select>
                </label>
                <label className="inline-flex items-center">
                    <span className="sr-only">{t('pipelines.pipeline')}</span>
                    <select value={query.pipeline ?? 'all'} onChange={(e) => set({pipeline: e.target.value === 'all' ? undefined : e.target.value as TasksQuery['pipeline']})}
                            className={`h-9 pl-3 pr-8 rounded-full border bg-white text-[13px] font-medium text-neutralBlack-900 ${query.pipeline && query.pipeline !== 'all' ? 'border-neutralBlack-900' : 'border-neutralBlack-200'}`}>
                        <option value="all">{t('pipelines.tasks.allPipelines')}</option>
                        <option value="activation">{t('pipelines.activation')}</option>
                        <option value="conversion">{t('pipelines.conversion.name')}</option>
                    </select>
                </label>
                <MultiSelectFilter label={t('pipelines.filters.market')} values={query.market}
                                   onChange={(market) => set({market})}
                                   options={markets.map((m) => ({value: m, label: m}))}/>
                <label className="inline-flex items-center">
                    <span className="sr-only">{t('pipelines.tasks.kind')}</span>
                    <select value={query.kind ?? ''} onChange={(e) => set({kind: (e.target.value || undefined) as TasksQuery['kind']})}
                            className={`h-9 pl-3 pr-8 rounded-full border bg-white text-[13px] font-medium text-neutralBlack-900 ${query.kind ? 'border-neutralBlack-900' : 'border-neutralBlack-200'}`}>
                        <option value="">{t('pipelines.tasks.kind')}</option>
                        <option value="HUMAN_TOUCH">{t('pipelines.tasks.kindHuman')}</option>
                        <option value="FIX_CONTACT">{t('pipelines.touch.FIX_CONTACT')}</option>
                    </select>
                </label>
                <span className="inline-flex items-center gap-1.5 text-[12.5px] text-neutralBlack-700">
                    <label htmlFor="pl-tasks-from">{t('pipelines.tasks.scheduled')}</label>
                    <input id="pl-tasks-from" type="date" aria-label={t('pipelines.tasks.from')} value={millisToDateInput(query.from, true)}
                           onChange={(e) => set({from: dateInputToUtcMidnight(e.target.value)})} className={dateClass(query.from != null)}/>
                    <span aria-hidden="true">–</span>
                    <input type="date" aria-label={t('pipelines.tasks.to')} value={millisToDateInput(query.to, true)}
                           onChange={(e) => set({to: dateInputToUtcMidnight(e.target.value)})} className={dateClass(query.to != null)}/>
                </span>
                {hasFilters && (
                    <button type="button" onClick={() => onQueryChange({assignee: query.assignee})}
                            className="text-[12.5px] font-medium text-neutralBlack-900 underline underline-offset-[3px] px-1 hover:text-neutralBlack-600 transition-colors duration-200 ease-out">
                        {t('pipelines.filters.clear')}
                    </button>
                )}
                <span className="flex-1"/>
                {isFetchingTasks && !isLoadingTasks && <span className="text-[12.5px] text-neutralBlack-600" aria-live="polite">{t('pipelines.board.refreshing')}</span>}
            </div>

            <div className="flex gap-3 flex-wrap flex-none">
                {(['overdue', 'today', 'upcoming'] as Section[]).map((key) => (
                    <div key={key} className="min-w-[150px] rounded-xl border border-neutralBlack-200 bg-white px-4 py-3">
                        <div className="text-[12px] font-medium text-neutralBlack-600">{t(`pipelines.tasks.section.${key}`)}</div>
                        <div className={`mt-2 text-[26px] font-semibold tracking-[-.02em] leading-none ${key === 'overdue' && sections.overdue.length > 0 ? 'text-[#B42318]' : 'text-neutralBlack-900'}`}>
                            {isLoadingTasks ? '–' : sections[key].length}
                        </div>
                    </div>
                ))}
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto rounded-[14px] border border-neutralBlack-200 bg-white">
                {isLoadingTasks ? (
                    <div className="p-4 flex flex-col gap-2.5" aria-busy="true">
                        {Array.from({length: 5}).map((_, i) => <div key={i} className="h-14 rounded-lg bg-neutralBlack-50 animate-pulse"/>)}
                    </div>
                ) : tasksError ? (
                    <div role="alert" className="py-14 px-6 flex flex-col items-center gap-3 text-center">
                        <span className="text-[14px] font-semibold text-neutralBlack-900">{t('pipelines.tasks.error')}</span>
                        <button type="button" onClick={() => refetchTasks()}
                                className="h-9 px-4 rounded-full bg-neutralBlack-900 text-white text-[13px] font-medium hover:bg-neutralBlack-700 transition-colors duration-200 ease-out">
                            {t('pipelines.retry')}
                        </button>
                    </div>
                ) : tasks.length === 0 ? (
                    <p className="py-14 px-6 text-center text-[13px] text-neutralBlack-600">{t('pipelines.tasks.empty')}</p>
                ) : (
                    <>
                        {(['overdue', 'today', 'upcoming'] as Section[]).filter((key) => sections[key].length > 0).map((key) => (
                            <section key={key} aria-labelledby={`pl-tasks-${key}`}>
                                <h2 id={`pl-tasks-${key}`} className="flex items-center gap-2 h-10 px-4 bg-neutralBlack-50 border-b border-neutralBlack-100">
                                    <span className={`text-[11px] font-semibold tracking-[.06em] uppercase ${key === 'overdue' ? 'text-[#B42318]' : 'text-neutralBlack-700'}`}>
                                        {t(`pipelines.tasks.section.${key}`)}
                                    </span>
                                    <span className="text-[11.5px] font-medium text-neutralBlack-600">{sections[key].length}</span>
                                </h2>
                                <ul>
                                    {sections[key].map((task) => {
                                        const due = relativeDue(taskDue(task), t)
                                        const overdue = key === 'overdue'
                                        const assigneeMember = team.find((m) => m.id === task.touchpoint.assignee)
                                        const name = cardTravelerName(task.card, t)
                                        return (
                                            <li key={task.touchpoint.id ?? `${taskPipeline(task)}-${task.card.id}-${task.touchpoint.key}`}>
                                                <button type="button" onClick={() => onOpenTask(task)}
                                                        aria-label={t('pipelines.tasks.open', {task: touchLabel(task.touchpoint.key, null, t), name})}
                                                        className={`w-full text-left flex items-center gap-4 px-4 py-3 border-b border-neutralBlack-100 transition-colors duration-200 ease-out ${
                                                            task.card.id === selectedId ? 'bg-neutralBlack-100' : overdue ? 'bg-[#FFF7F6] hover:bg-[#FDECEA]' : 'hover:bg-neutralBlack-50'}`}>
                                                    <span className="flex-[1.2_1_0] min-w-[200px]">
                                                        <span className="flex items-center gap-1.5">
                                                            <Countries countries={task.card.trip.countries}/>
                                                            <span className="ml-0.5 text-[14px] font-medium text-neutralBlack-900 truncate">{name}</span>
                                                        </span>
                                                        <span className="block mt-0.5 text-[12px] text-neutralBlack-600 truncate">{task.card.agencyName ?? task.card.agencyId ?? '—'}</span>
                                                    </span>
                                                    <span className="flex-[1.1_1_0] min-w-[180px] flex items-center gap-2 flex-wrap">
                                                        <span className="text-[13.5px] font-medium text-neutralBlack-900">{touchLabel(task.touchpoint.key, null, t)}</span>
                                                        {task.kind === 'FIX_CONTACT'
                                                            ? <Tag tone="red">{t('pipelines.badge.contactFailed')}</Tag>
                                                            : <Tag>{t(`pipelines.stage.${task.card.crmStage}`)}</Tag>}
                                                        <Tag tone={taskPipeline(task) === 'conversion' ? 'teal' : 'grey'}>
                                                            {taskPipeline(task) === 'conversion' ? t('pipelines.conversion.name') : t('pipelines.activation')}
                                                        </Tag>
                                                        <ChannelIcon channel={task.touchpoint.channel}/>
                                                    </span>
                                                    <span className="w-[56px] flex-none"><MarketChip market={task.card.market}/></span>
                                                    <span className="w-[130px] flex-none flex items-center gap-1.5 min-w-0">
                                                        {task.touchpoint.assignee
                                                            ? <><Avatar member={assigneeMember} name={task.touchpoint.assignee} size={20}/><span className="text-[12px] text-neutralBlack-700 truncate">{assigneeMember?.name ?? task.touchpoint.assignee}</span></>
                                                            : <span className="text-[12px] text-neutralBlack-600">{t('pipelines.unassigned')}</span>}
                                                    </span>
                                                    <span className={`w-[130px] flex-none text-right text-[12.5px] ${overdue ? 'text-[#B42318] font-semibold' : 'text-neutralBlack-700'}`}
                                                          title={formatDateTime(taskDue(task))}>
                                                        {due?.text ?? '—'}
                                                    </span>
                                                </button>
                                            </li>
                                        )
                                    })}
                                </ul>
                            </section>
                        ))}
                        {hasMoreTasks && (
                            <div className="p-3 flex justify-center">
                                <button type="button" onClick={() => loadMoreTasks()} disabled={isFetchingMoreTasks}
                                        className="h-9 px-4 rounded-full border border-neutralBlack-200 text-[13px] font-medium text-neutralBlack-900 hover:bg-neutralBlack-100 transition-colors duration-200 ease-out">
                                    {isFetchingMoreTasks ? <Spinner/> : t('pipelines.tasks.loadMore')}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}
